import { useSelector } from 'react-redux';
import { getErrorMessages } from '../state/errorMessagesSlice';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { FormattedIntlCurrency } from 'I18n/components/FormattedIntlCurrency';
import FormattedJSXMessage from 'I18n/components/FormattedJSXMessage';
import { useCustomFormFields } from './useCustomFormFields';
import { jsx as _jsx } from "react/jsx-runtime";
export const useGetErrorMessagesAndTranslations = () => {
  const errorMessages = useSelector(getErrorMessages);
  const errorMessageMap = new Map();
  const {
    orderedCustomFormFieldErrorKeys
  } = useCustomFormFields();
  const BASE_ERROR_MESSAGE_ORDER = ['emptyEmailAddress', 'invalidEmailAddress', ...orderedCustomFormFieldErrorKeys, 'emptyFirstName', 'emptyLastName', 'emptyCountry', 'emptyAddressLine1', 'emptyCity', 'emptyCityTownVillage', 'emptyState', 'emptyStateProvinceRegion', 'emptyPostalCode', 'invalidStripePaymentMethod', 'emptyAccountHolderName', 'emptyNameOnCard', 'invalidDiscountCode', 'recurringTotalOverMax', 'recurringTotalBelowMin', 'totalOverMax', 'totalBelowMin', 'allOptionalItemsUnselected', 'collectPMOFNotAcknowledged', 'clickToCancelNotAcknowledged', 'merchantTermsNotAcknowledged'];
  Object.entries(errorMessages).forEach(([key, value]) => {
    var _value$totalsOptions, _value$totalsOptions2, _value$totalsOptions3, _value$totalsOptions4, _value$totalsOptions5, _value$totalsOptions6, _value$totalsOptions7, _value$totalsOptions8;
    if (value !== null && value !== void 0 && value.isError) {
      switch (key) {
        case 'emptyEmailAddress':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyEmailAddress"
          }, key));
          break;
        case 'invalidEmailAddress':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.contactInfo.invalidEmailAddress"
          }, key));
          break;
        case 'emptyFirstName':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyFirstName"
          }, key));
          break;
        case 'emptyLastName':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyLastName"
          }, key));
          break;
        case `empty${value.customFormFieldLabel}`:
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyCustomFormField",
            options: {
              fieldName: value.customFormFieldLabel
            }
          }, key));
          break;
        case `invalidPhoneNumber_${value.customFormFieldLabel}`:
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.invalidPhoneNumber",
            options: {
              phoneNumberType: value.customFormFieldLabel
            }
          }, key));
          break;
        // shipping and billing fields have to have the same key to prevent the
        // same error message being shown twice
        case 'emptyShippingState':
        case 'emptyBillingState':
          errorMessageMap.set('emptyState', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyState"
          }, key));
          break;
        case 'emptyShippingStateProvinceRegion':
        case 'emptyBillingStateProvinceRegion':
          errorMessageMap.set('emptyStateProvinceRegion', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyStateProvinceRegion"
          }, key));
          break;
        case 'emptyShippingAddressLine1':
        case 'emptyBillingAddressLine1':
          errorMessageMap.set('emptyAddressLine1', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyAddressLine1"
          }, key));
          break;
        case 'emptyShippingCity':
        case 'emptyBillingCity':
          errorMessageMap.set('emptyCity', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyCity"
          }, key));
          break;
        case 'emptyShippingCityTownVillage':
        case 'emptyBillingCityTownVillage':
          errorMessageMap.set('emptyCityTownVillage', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyCityTownVillage"
          }, key));
          break;
        case 'emptyShippingPostalCode':
        case 'emptyBillingPostalCode':
          errorMessageMap.set('emptyPostalCode', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyPostalCode"
          }, key));
          break;
        case 'emptyShippingCountry':
        case 'emptyBillingCountry':
          errorMessageMap.set('emptyCountry', /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyCountry"
          }, key));
          break;
        case 'emptyAccountHolderName':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyAccountHolderName"
          }, key));
          break;
        case 'emptyNameOnCard':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.emptyNameOnCard"
          }, key));
          break;
        case 'collectPMOFNotAcknowledged':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.paymentMethodOnFile.errorMsg"
          }, key));
          break;
        case 'clickToCancelNotAcknowledged':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.merchantTermsOfService.clickToCancel.errorMessage"
          }, key));
          break;
        case 'invalidStripePaymentMethod':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.inputError.invalidStripePaymentMethod"
          }, key));
          break;
        case 'merchantTermsNotAcknowledged':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.merchantTermsOfService.acknowledgementRequiredErrorMessage"
          }, key));
          break;
        case 'invalidDiscountCode':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.invalidDiscountCodeMessage"
          }, key));
          break;
        case 'recurringTotalOverMax':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedJSXMessage, {
            elements: {
              Price: FormattedIntlCurrency
            },
            message: "payments.recurringTotalOverMaximum_jsx",
            options: {
              value: (_value$totalsOptions = value.totalsOptions) === null || _value$totalsOptions === void 0 ? void 0 : _value$totalsOptions.maximumAmount,
              currencyCode: (_value$totalsOptions2 = value.totalsOptions) === null || _value$totalsOptions2 === void 0 ? void 0 : _value$totalsOptions2.currencyCode
            }
          }, key));
          break;
        case 'totalOverMax':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedJSXMessage, {
            elements: {
              Price: FormattedIntlCurrency
            },
            message: "payments.totalOverMaximum_jsx",
            options: {
              value: (_value$totalsOptions3 = value.totalsOptions) === null || _value$totalsOptions3 === void 0 ? void 0 : _value$totalsOptions3.maximumAmount,
              currencyCode: (_value$totalsOptions4 = value.totalsOptions) === null || _value$totalsOptions4 === void 0 ? void 0 : _value$totalsOptions4.currencyCode
            }
          }, key));
          break;
        case 'recurringFutureTotalBelowMin':
        case 'recurringDueNowTotalBelowMin':
          errorMessageMap.set('recurringTotalBelowMin', /*#__PURE__*/_jsx(FormattedJSXMessage, {
            elements: {
              Price: FormattedIntlCurrency
            },
            message: "payments.recurringValidationError_jsx",
            options: {
              value: (_value$totalsOptions5 = value.totalsOptions) === null || _value$totalsOptions5 === void 0 ? void 0 : _value$totalsOptions5.minimumAmount,
              currencyCode: (_value$totalsOptions6 = value.totalsOptions) === null || _value$totalsOptions6 === void 0 ? void 0 : _value$totalsOptions6.currencyCode
            }
          }, key));
          break;
        case 'totalBelowMin':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedJSXMessage, {
            elements: {
              Price: FormattedIntlCurrency
            },
            message: "payments.validationError_jsx",
            options: {
              value: (_value$totalsOptions7 = value.totalsOptions) === null || _value$totalsOptions7 === void 0 ? void 0 : _value$totalsOptions7.minimumAmount,
              currencyCode: (_value$totalsOptions8 = value.totalsOptions) === null || _value$totalsOptions8 === void 0 ? void 0 : _value$totalsOptions8.currencyCode
            }
          }, key));
          break;
        case 'allOptionalItemsUnselected':
          errorMessageMap.set(key, /*#__PURE__*/_jsx(FormattedMessage, {
            message: "payments.optionalTotalUnderMinimum"
          }, key));
          break;
        default:
          break;
      }
    }
  });
  const orderedErrorMessageMap = new Map();
  BASE_ERROR_MESSAGE_ORDER.forEach(key => {
    const message = errorMessageMap.get(key);
    if (message) {
      orderedErrorMessageMap.set(key, message);
    }
  });
  return orderedErrorMessageMap;
};